import React, { useState, useEffect, useMemo } from 'react'
import { T, Spinner, Empty, SearchBar, searchMatch, gradeColor, gradeLetter } from './ui'
import { getNFLCheatsheet } from './api'

/**
 * Matchup Cheatsheet — the whole week's matchups as sortable category tables
 * (Passing / Rushing / Receiving / Red Zone / Alignment). Each row is one offense vs
 * its opponent this week: the matchup grade (opponent-adjusted, higher = better for the
 * offense) next to the opponent defense's season softness rank (1 = softest = the spot to
 * attack). The fast "where are the soft spots this week" scan. Reads /nfl/cheatsheet.
 * Football research — informational only, no picks or wagers.
 */

// Rank 1 = softest defense (best to attack) = green; 32 = toughest = red.
const rankColor = r => r == null ? T.faint : r <= 8 ? T.green : r <= 16 ? T.mint : r <= 24 ? T.amber : T.red
const LEAN_COLOR = { Vertical: T.blue, Short: T.amber, Balanced: T.muted2 }
// Ordinal suffix so a rank reads as a rank ("6th"), not a raw stat ("6").
const ordSuffix = n => { const v = n % 100; return (v >= 11 && v <= 13) ? 'th' : (['th','st','nd','rd'][n % 10] || 'th') }

// Plain-language "how to read" legend — the fix for the dense-numbers confusion. Says
// what the Matchup grade is and which direction the defense rank runs, with a color bar.
function Legend() {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap',
      background: T.card2, border: `1px solid ${T.border}`, borderRadius: 9, padding: '8px 11px', marginBottom: 8 }}>
      <span style={{ fontSize: 10.5, color: T.muted }}><b style={{ color: T.text }}>Matchup</b> = spot quality (higher = better)</span>
      <span style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 10.5, color: T.muted }}>
        Opp defense:
        <span style={{ color: T.green, fontWeight: 800 }}>1st easiest</span>
        <span style={{ width: 54, height: 6, borderRadius: 3, background: `linear-gradient(90deg, ${T.green}, ${T.mint}, ${T.amber}, ${T.red})` }} />
        <span style={{ color: T.red, fontWeight: 800 }}>32nd tough</span>
      </span>
    </div>
  )
}

function Chip({ on, onClick, children }) {
  return (
    <button onClick={onClick} style={{ cursor: 'pointer', fontSize: 11.5, fontWeight: 800,
      padding: '6px 12px', borderRadius: 9, whiteSpace: 'nowrap',
      border: `1px solid ${on ? T.green : T.border}`, background: on ? T.greenDim : T.card,
      color: on ? T.green : T.muted }}>{children}</button>
  )
}

function MiniChip({ text, color }) {
  if (!text) return <span style={{ color: T.faint }}>—</span>
  return <span style={{ fontSize: 10, fontWeight: 800, color, background: `${color}1A`,
    border: `1px solid ${color}55`, borderRadius: 6, padding: '2px 6px', whiteSpace: 'nowrap' }}>{text}</span>
}

// One data cell, formatted + colored by the column's declared kind.
function Cell({ col, row }) {
  const v = row[col.key]
  const mono = { fontFamily: T.mono, fontWeight: 800, fontSize: 13 }
  if (col.kind === 'grade') {
    if (v == null) return <span style={{ color: T.faint }}>—</span>
    const c = gradeColor(v)
    return <span style={{ ...mono, color: c }}>{v}<span style={{ fontSize: 10, fontWeight: 700, marginLeft: 3, opacity: 0.8 }}>{gradeLetter(v)}</span></span>
  }
  if (col.kind === 'rank') {
    if (v == null) return <span style={{ color: T.faint }}>—</span>
    return <span style={{ ...mono, color: rankColor(v) }}>{v}<span style={{ fontSize: 10, fontWeight: 700, opacity: 0.7 }}>{ordSuffix(v)}</span></span>
  }
  if (col.kind === 'pct') return <span style={mono}>{v == null ? '—' : `${Math.round(v * 100)}%`}</span>
  if (col.kind === 'num') return <span style={{ ...mono, color: v == null ? T.faint : T.muted2 }}>{v == null ? '—' : v}</span>
  if (col.kind === 'lean') {
    return <div style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
      <MiniChip text={v} color={LEAN_COLOR[v] || T.muted} />
      {row.adot != null && <span style={{ fontFamily: T.mono, fontSize: 10, color: T.faint }}>{row.adot}</span>}
    </div>
  }
  if (col.kind === 'verdict') return <MiniChip text={v} color={T.green} />
  return <span style={mono}>{v == null ? '—' : v}</span>
}

// Simple-mode summary — the softest defense to attack in each category, so Simple isn't
// just Best Spots + empty space. Computed from the category rows the endpoint already
// returns; each line taps through to that game. Pro users get the full sortable tables.
function SimpleSummary({ cats, onGame }) {
  const CATS = [['passing', 'Passing', 'pass_rank'], ['rushing', 'Rushing', 'rush_rank'],
    ['receiving', 'Receiving', 'wr_rank'], ['redzone', 'Red Zone', 'rz_rank']]
  const byKey = Object.fromEntries((cats || []).map(c => [c.key, c]))
  const rows = CATS.map(([k, label, rk]) => {
    const cat = byKey[k]
    if (!cat) return null
    const cand = (cat.rows || []).filter(r => r[rk] != null).sort((a, b) => a[rk] - b[rk])[0]
    return cand ? { label, rk, ...cand } : null
  }).filter(Boolean)
  if (!rows.length) return null
  return (
    <div style={{ marginTop: 12 }}>
      <div style={{ fontSize: 11, fontWeight: 800, color: T.muted, textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 8 }}>Softest defense by category</div>
      <div style={{ background: T.card, border: `1px solid ${T.border}`, borderRadius: 12, overflow: 'hidden' }}>
        {rows.map((r, i) => {
          const rk = r[r.rk]
          const tap = onGame && r.game_id ? () => onGame({ game_id: r.game_id }) : undefined
          return (
            <button key={i} onClick={tap} style={{ display: 'flex', width: '100%', alignItems: 'center', justifyContent: 'space-between',
              gap: 10, textAlign: 'left', cursor: tap ? 'pointer' : 'default', background: 'transparent', border: 'none',
              borderTop: i ? `1px solid ${T.border}` : 'none', color: T.text, padding: '11px 13px' }}>
              <span style={{ minWidth: 0 }}>
                <span style={{ fontSize: 12.5, fontWeight: 800, display: 'block' }}>{r.label}</span>
                <span style={{ fontSize: 11, color: T.muted }}>Attack <b style={{ color: rankColor(rk) }}>{r.opp}</b> · {rk}{ordSuffix(rk)}-easiest · {r.team} {r.at ? '@' : 'vs'} {r.opp}</span>
              </span>
              <span style={{ color: T.faint, fontSize: 15 }}>›</span>
            </button>
          )
        })}
      </div>
    </div>
  )
}

function Header({ cat, sortKey, sortDir, onSort }) {
  const caret = k => sortKey === k ? (sortDir === 'asc' ? ' ▲' : ' ▼') : ''
  const th = { fontSize: 10.5, fontWeight: 800, color: T.muted, letterSpacing: '0.04em',
    textTransform: 'uppercase', padding: '8px 0 9px', cursor: 'pointer', whiteSpace: 'nowrap' }
  return (
    <div style={{ display: 'flex', alignItems: 'flex-end', borderBottom: `1px solid ${T.border}`, paddingBottom: 0 }}>
      <div style={{ ...th, flex: 1, cursor: 'pointer', minWidth: 104 }} onClick={() => onSort('team')}>Matchup{caret('team')}</div>
      {cat.columns.map(c => (
        <div key={c.key} title={c.help} style={{ ...th, width: 58, textAlign: 'right' }} onClick={() => onSort(c.key)}>{c.label}{caret(c.key)}</div>
      ))}
    </div>
  )
}

function Row({ cat, row, i, onGame }) {
  const tap = onGame && row.game_id ? () => onGame({ game_id: row.game_id }) : undefined
  return (
    <div onClick={tap} style={{ display: 'flex', alignItems: 'center', padding: '9px 0',
      borderTop: i === 0 ? 'none' : `1px solid ${T.border}`, cursor: tap ? 'pointer' : 'default' }}>
      <div style={{ flex: 1, minWidth: 104 }}>
        <div style={{ fontSize: 13.5, fontWeight: 800 }}>{row.team}</div>
        <div style={{ fontSize: 10.5, color: T.muted, fontWeight: 600 }}>{row.at ? '@ ' : 'vs '}{row.opp}</div>
      </div>
      {cat.columns.map(c => (
        <div key={c.key} style={{ width: 58, textAlign: 'right' }}><Cell col={c} row={row} /></div>
      ))}
    </div>
  )
}

// Best spots — the "where are the good bets this week" lead. The strongest offense
// matchups across the categories, each pointing to its game (tap to open it).
function BestSpots({ spots, onGame }) {
  if (!spots || !spots.length) return null
  return (
    <div style={{ marginBottom: 14 }}>
      <div style={{ fontSize: 12, fontWeight: 800, color: T.text, marginBottom: 8 }}>🎯 Best spots this week</div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 7 }}>
        {spots.slice(0, 6).map((s, i) => {
          const c = gradeColor(s.grade)
          const why = s.rank != null ? `${s.opp} ${s.rank}${ordSuffix(s.rank)}-easiest matchup` : `${s.opp} defense`
          const tap = onGame && s.game_id ? () => onGame({ game_id: s.game_id }) : undefined
          return (
            <button key={i} onClick={tap} style={{ display: 'flex', alignItems: 'center', gap: 11, width: '100%',
              textAlign: 'left', cursor: tap ? 'pointer' : 'default', background: T.card, border: `1px solid ${c}44`,
              borderRadius: 11, padding: '10px 12px', color: T.text }}>
              <span style={{ fontFamily: T.mono, fontWeight: 900, fontSize: 16, color: c, width: 30, flexShrink: 0, textAlign: 'center' }}>{gradeLetter(s.grade)}</span>
              <span style={{ minWidth: 0, flex: 1 }}>
                <span style={{ display: 'block', fontSize: 13.5, fontWeight: 800, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{s.team} · {s.unit} <span style={{ color: T.muted, fontWeight: 600 }}>{s.at ? '@' : 'vs'} {s.opp}</span></span>
                <span style={{ display: 'block', fontSize: 11, color: c }}>{why}</span>
              </span>
              {tap && <span style={{ color: T.faint, fontSize: 17 }}>›</span>}
            </button>
          )
        })}
      </div>
    </div>
  )
}

function copyText(cat) {
  const lines = [`${cat.label} cheatsheet — where to attack (rank 1 = softest defense)`]
  cat._sorted.forEach(r => {
    const parts = cat.columns.map(c => {
      const v = r[c.key]
      if (v == null) return null
      if (c.kind === 'pct') return `${c.label} ${Math.round(v * 100)}%`
      return `${c.label} ${v}`
    }).filter(Boolean)
    lines.push(`${r.team} ${r.at ? '@' : 'vs'} ${r.opp} — ${parts.join(', ')}`)
  })
  return lines.join('\n')
}

function CopyBtn({ text }) {
  const [done, setDone] = useState(false)
  const copy = () => {
    const ok = () => { setDone(true); setTimeout(() => setDone(false), 1500) }
    try {
      if (navigator.clipboard?.writeText) navigator.clipboard.writeText(text).then(ok, ok)
      else { const t = document.createElement('textarea'); t.value = text; document.body.appendChild(t); t.select(); document.execCommand('copy'); t.remove(); ok() }
    } catch (_) { ok() }
  }
  return (
    <button onClick={copy} style={{ cursor: 'pointer', fontFamily: T.mono, fontWeight: 700, fontSize: 10.5,
      padding: '4px 9px', borderRadius: 8, color: done ? '#04140D' : T.green,
      background: done ? T.green : T.greenDim, border: `1px solid ${T.green}55`, whiteSpace: 'nowrap' }}>
      {done ? '✓ Copied' : '⧉ Copy'}
    </button>
  )
}

export default function Cheatsheet({ season, week, onGame, pro }) {
  const [d, setD] = useState(null)
  const [loading, setLoading] = useState(true)
  const [sample, setSample] = useState(false)
  const [active, setActive] = useState('passing')
  const [q, setQ] = useState('')
  const [sort, setSort] = useState(null)      // {key, dir} — overrides the category default

  useEffect(() => {
    let a = true; setLoading(true); setSort(null)
    getNFLCheatsheet(season, week).then(r => { if (!a) return; setD(r); setSample(!!r._sample); setLoading(false) })
    return () => { a = false }
  }, [season, week])

  const cats = d?.categories || []
  const cat = cats.find(c => c.key === active) || cats[0]

  const sorted = useMemo(() => {
    if (!cat) return []
    const key = sort?.key || cat.sort || 'grade'
    const dir = sort?.dir || cat.sort_dir || 'desc'
    const rows = (cat.rows || []).filter(searchMatch(q, r => [r.team, r.opp]))
    const num = v => (typeof v === 'number' ? v : null)
    return [...rows].sort((x, y) => {
      if (key === 'team') return dir === 'asc' ? x.team.localeCompare(y.team) : y.team.localeCompare(x.team)
      const a = num(x[key]), b = num(y[key])
      if (a == null && b == null) return 0
      if (a == null) return 1            // nulls always last
      if (b == null) return -1
      return dir === 'asc' ? a - b : b - a
    })
  }, [cat, sort, q])

  const onSort = k => setSort(s => {
    const curKey = s?.key || cat?.sort || 'grade'
    const curDir = s?.dir || cat?.sort_dir || 'desc'
    if (curKey === k) return { key: k, dir: curDir === 'asc' ? 'desc' : 'asc' }
    // sensible default direction per column: grade high-first, ranks low-first, team A-Z
    return { key: k, dir: k === 'grade' || k === 'rz_rate' ? 'desc' : k === 'team' ? 'asc' : 'asc' }
  })

  if (loading) return <Spinner />
  if (!cats.length) return <Empty message="No matchups for this week yet" />

  const catForCopy = cat ? { ...cat, _sorted: sorted } : null

  return (
    <div>
      {sample && <div style={{ fontSize: 10, color: T.amber, marginBottom: 8, letterSpacing: '0.05em' }}>● Live data unavailable right now — check back shortly</div>}
      {d?.carryover && <div style={{ fontSize: 10, color: T.amber, marginBottom: 8 }}>● Early-season — matchup data carries over last season until the league has played</div>}

      <BestSpots spots={d?.top_spots} onGame={onGame} />

      {/* Full matchup tables + legend — shown to everyone (not gated behind Pro). */}
      <div style={{ display: 'flex', gap: 6, marginBottom: 10, overflowX: 'auto', paddingBottom: 2, scrollbarWidth: 'none' }}>
        {cats.map(c => <Chip key={c.key} on={active === c.key} onClick={() => { setActive(c.key); setSort(null) }}>{c.label}</Chip>)}
      </div>

      {cat && (
        <>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10, marginBottom: 8 }}>
            <div style={{ fontSize: 11, color: T.muted, lineHeight: 1.4, flex: 1 }}>{cat.hint}</div>
            {sorted.length > 0 && <CopyBtn text={copyText(catForCopy)} />}
          </div>
          <SearchBar value={q} onChange={setQ} placeholder="Search team or opponent…" />
          {cat.note && <div style={{ fontSize: 11, color: T.amber, margin: '4px 0 8px' }}>{cat.note}</div>}
          <Legend />
          <div style={{ background: T.card, border: `1px solid ${T.border}`, borderRadius: 12, padding: '10px 14px' }}>
            <Header cat={cat} sortKey={sort?.key || cat.sort} sortDir={sort?.dir || cat.sort_dir || 'desc'} onSort={onSort} />
            {!sorted.length ? <Empty message={q ? `No matchups match "${q}"` : 'No rows'} />
              : sorted.map((r, i) => <Row key={r.team} cat={cat} row={r} i={i} onGame={onGame} />)}
          </div>
        </>
      )}

      <div style={{ fontSize: 10, color: T.faint, marginTop: 8, lineHeight: 1.5 }}>
        Matchup = opponent-adjusted grade (higher = better for the offense). Defense ranks are season-to-date,
        1 = allows the most (softest). Tap a column to sort. Informational only · 21+.
      </div>
    </div>
  )
}
